import React, { ReactElement } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import classNames from "classnames";
import { useWebTranslation } from "../../translations";
import { getOrg, Org } from "../../utils/site";
import { localeHref } from "../../utils/localeHref";
import { LanguageSelector } from "../LanguageSelector/LanguageSelector";
import { packages } from "../InsurancePackages";
import {
  gbrIpidByPlan,
  gbrPolicyWording,
  prepurchaseInfoInsurance,
  prepurchaseInfoLassie,
} from "../../components-shared/insuranceData";
import styles from "./FooterRedesign.module.css";
import {
  AppStoreIcon,
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  PlayStoreIcon,
  YoutubeIcon,
} from "./icons";

/**
 * 2026 refresh footer.
 *
 * Content is the SAME per-market links as the pre-redesign footer, just moved
 * into the new columns (Our Offer · Support · Lassie) + legal block. Both the
 * label AND the href come from Lokalise wherever the key has them:
 *   - `labelKey`  → t() for the visible label (a `.text` sub-key or a string key)
 *   - `hrefKey`   → t() for the href, resolved with the org context
 *                   (`href_ger`/`href_fra`/`href_swe`), exactly like the old footer
 * Links whose Lokalise key has no `.href` (navigation.*, insurance_documents.*,
 * factsheets, insurance_terms, purchase_info) keep a hardcoded `href` — those
 * were hardcoded in the old footer too. English `defaultValue`s let everything
 * render before any brand-new key is translated.
 */

type FooterLink = {
  /** Lokalise key for the label (a `.text` sub-key or a string key). */
  labelKey: string;
  defaultLabel: string;
  /**
   * Fallback href, used only when no Lokalise source resolves. For links that
   * existed in the old footer this mirrors the old expression (a literal the
   * old footer also used); for NEW links it's the fallback until the flagged
   * key is added in Lokalise.
   */
  href?: string;
  /** Lokalise key for the href (org-context resolved). Takes precedence. */
  hrefKey?: string;
  /** Reuses the old footer's dynamic factsheet href: packages().dog[i].ipid. */
  dogIpidIndex?: number;
  /** Opens the cookie-consent manager instead of navigating. */
  consent?: boolean;
  /** Forces tier order (factsheets) instead of alphabetical sorting. */
  order?: number;
  /** Renders bold + underlined to stand out from the other links. */
  bold?: boolean;
  /** Pins to the bottom of its column, after the alphabetically-sorted links. */
  pinLast?: boolean;
};

type Social = "facebook" | "instagram" | "linkedin" | "youtube";

type Market = {
  offer: FooterLink[];
  support: FooterLink[];
  company: FooterLink[];
  legal: FooterLink[];
  /** SE-only regulatory paragraph. */
  legalParagraph?: { key: string; defaultValue: string };
  app: { appStore: string; googlePlay: string };
  social: Partial<Record<Social, string>>;
};

const aboutLink = (href: string, hrefKey?: string): FooterLink => ({
  labelKey: "navigation.about_us",
  defaultLabel: "About Us",
  href,
  ...(hrefKey ? { hrefKey } : {}),
});
// Contact label reuses navigation.contact_us. footer.contact_us.href exists in
// Lokalise only as a flat dotted top-level key, which i18next cannot resolve
// with its default "." separator — hence the literal fallback. Once the nested
// key lands, the hrefKey resolves per locale and the literal stops being used.
const contactLink = (href: string, hrefKey?: string): FooterLink => ({
  labelKey: "navigation.contact_us",
  defaultLabel: "Contact",
  href,
  ...(hrefKey ? { hrefKey } : {}),
});
const howToClaimLink = (href: string): FooterLink => ({
  // NEW key — no pre-redesign equivalent. Needs text + href in Lokalise;
  // fallbacks (English label + per-market href) apply until added.
  labelKey: "footer.how_to_claim.text",
  defaultLabel: "How to file a claim",
  hrefKey: "footer.how_to_claim.href",
  href,
});
const PRIVACY_SETTINGS: FooterLink = {
  labelKey: "global.privacy_settings",
  defaultLabel: "Privacy Settings",
  consent: true,
};
// Right-of-withdrawal link in the Support column. Bold + underlined to stand
// out from the other links; label and href both come from Lokalise
// (footer.withdrawal.text / .href).
const withdrawalLink: FooterLink = {
  labelKey: "footer.withdrawal.text",
  defaultLabel: "Right of withdrawal",
  hrefKey: "footer.withdrawal.href",
  bold: true,
  pinLast: true,
};

const se: Market = {
  // NEW to the footer — old SE footer had no product links. Label reuses the
  // existing navigation key; href needs a new Lokalise key (footer.offer_*.href).
  offer: [
    {
      labelKey: "navigation.dog_insurance",
      defaultLabel: "Dog Insurance",
      hrefKey: "footer.offer_dog_insurance.href",
      href: "/hundforsakring",
    },
    {
      labelKey: "navigation.cat_insurance",
      defaultLabel: "Cat Insurance",
      hrefKey: "footer.offer_cat_insurance.href",
      href: "/kattforsakring",
    },
  ],
  support: [
    // REUSE: old footer used packages().dog[i].ipid for these.
    {
      labelKey: "footer.fact_sheet_mini",
      defaultLabel: "Fact sheet Small",
      dogIpidIndex: 0,
      order: 1,
    },
    {
      labelKey: "footer.fact_sheet_between",
      defaultLabel: "Fact Sheet Medium",
      dogIpidIndex: 1,
      order: 2,
    },
    {
      labelKey: "footer.fact_sheet_big",
      defaultLabel: "Fact sheet Big",
      dogIpidIndex: 2,
      order: 3,
    },
    // NEW to the footer — label reuses navigation.faq; href needs a key.
    {
      labelKey: "navigation.faq",
      defaultLabel: "FAQ",
      hrefKey: "footer.faq.href",
      href: "/faq",
    },
    // NEW to the footer — needs footer.how_to_claim (text + href).
    howToClaimLink("/vid-skada"),
    // REUSE: old footer used the prepurchaseInfoInsurance constant.
    {
      labelKey: "footer.purchase_info",
      defaultLabel: "Pre-sale Information",
      href: prepurchaseInfoInsurance,
    },
    // NEW key — footer.purchase_info_om_lassie should carry this copy in
    // Lokalise; until then the Swedish defaultLabel renders (SE-only link).
    {
      labelKey: "footer.purchase_info_om_lassie",
      defaultLabel: "Förköpsinformation om Lassie",
      href: prepurchaseInfoLassie,
    },
    withdrawalLink,
  ],
  company: [
    // The literals are the Swedish slugs, so they were also what the ENGLISH
    // tree linked to — /en/... never entered into it and English visitors
    // landed on Swedish pages. Both keys carry a per-locale value in Lokalise
    // (sv: /om-oss, en_GB: /about-us), so the hrefKey resolves correctly for
    // each tree; the literals remain only as fallbacks.
    aboutLink("/om-oss", "footer.about_us.href"),
    contactLink("/kontakta-oss", "footer.contact_us.href"),
    {
      labelKey: "footer.referral_program.text",
      defaultLabel: "Refer a friend",
      hrefKey: "footer.referral_program.href",
      href: "/kompisrabatt",
    },
  ],
  legal: [
    {
      labelKey: "footer.accessibility.text",
      defaultLabel: "Accessibility at Lassie",
      hrefKey: "footer.accessibility.href",
      href: "https://www.lassie.co/tillganglighet",
    },
    {
      labelKey: "footer.cookie_policy.text",
      defaultLabel: "Cookie Policy",
      hrefKey: "footer.cookie_policy.href",
      href: "https://www.lassie.co/cookiepolicy",
    },
    {
      labelKey: "footer.privacy_policy.text",
      defaultLabel: "Privacy Policy",
      hrefKey: "footer.privacy_policy.href",
      href: "https://www.lassie.co/integritetspolicy",
    },
    PRIVACY_SETTINGS,
    {
      // REUSE: old footer used the literal "/villkor".
      labelKey: "footer.insurance_terms",
      defaultLabel: "Terms and Conditions",
      href: "/villkor",
    },
    {
      // NEW keys — the terms of use webpage. Not footer.tac: that is the
      // insurance conditions page in DE. Needs text + href in Lokalise.
      labelKey: "footer.terms_of_use.text",
      defaultLabel: "Terms of use",
      hrefKey: "footer.terms_of_use.href",
      href: "/anvandarvillkor",
    },
    {
      labelKey: "footer.whistle-blowing.text",
      defaultLabel: "Whistleblowing",
      hrefKey: "footer.whistle-blowing.href",
      href: "https://www.lassie.co/visselblasning",
    },
  ],
  legalParagraph: {
    key: "footer.info",
    defaultValue:
      "Lassie AB is an insurance intermediary and is supervised by the Swedish Financial Supervisory Authority as a distributor of insurance in accordance with the Swedish Insurance Distribution Act (2018:1219). The insurer for Lassie AB's insurance is Dina Försäkring AB.",
  },
  app: {
    appStore: "https://apps.apple.com/se/app/lassie/id1533808157",
    googlePlay:
      "https://play.google.com/store/apps/details?id=com.teamlassie.lassieapp",
  },
  social: {
    facebook: "https://www.facebook.com/Lassie.Sweden",
    instagram: "https://www.instagram.com/lassie_se/",
    linkedin: "https://www.linkedin.com/company/lassieco/",
    youtube: "https://www.youtube.com/channel/UCBS4ArF2BlR2TixpopcOrnQ",
  },
};

const de: Market = {
  // NEW keys — the old DE footer linked IPID *documents*, not product pages.
  // footer.offer_* need text + href in Lokalise (Dog Liability reuses the
  // existing footer.ipid_liability label).
  offer: [
    {
      labelKey: "footer.offer_cat_health.text",
      defaultLabel: "Cat Health Insurance",
      hrefKey: "footer.offer_cat_health.href",
      href: "https://de.lassie.co/katzenversicherung/katzenkrankenversicherung",
    },
    {
      labelKey: "footer.offer_cat_op.text",
      defaultLabel: "Cat OP Insurance",
      hrefKey: "footer.offer_cat_op.href",
      href: "https://de.lassie.co/katzenversicherung/katzen-op-versicherung",
    },
    {
      labelKey: "footer.offer_dog_health.text",
      defaultLabel: "Dog Health Insurance",
      hrefKey: "footer.offer_dog_health.href",
      href: "https://de.lassie.co/hundeversicherung/hundekrankenversicherung",
    },
    {
      labelKey: "footer.ipid_liability.text",
      defaultLabel: "Dog Liability Insurance",
      hrefKey: "footer.offer_dog_liability.href",
      href: "https://de.lassie.co/hundeversicherung/hundehaftpflichtversicherung",
    },
    {
      labelKey: "footer.offer_dog_op.text",
      defaultLabel: "Dog OP Insurance",
      hrefKey: "footer.offer_dog_op.href",
      href: "https://de.lassie.co/hundeversicherung/hunde-op-versicherung",
    },
  ],
  support: [
    {
      labelKey: "footer.direct_settlement.text",
      defaultLabel: "Direct Billing for Veterinarians",
      hrefKey: "footer.direct_settlement.href",
      href: "https://de.lassie.co/direktabrechnung",
    },
    howToClaimLink("https://de.lassie.co/vorgehen-im-schadensfall"),
    {
      labelKey: "footer.pre-purchase_information.text",
      defaultLabel: "Initial Information",
      hrefKey: "footer.pre-purchase_information.href",
      href: "https://docs.lassie.co/ger/de/pre-purchase-information/latest/Erstinformation.pdf",
    },
    {
      labelKey: "footer.feedback.text",
      defaultLabel: "Praise and Criticism",
      hrefKey: "footer.feedback.href",
      href: "https://de.lassie.co/feedback",
    },
    withdrawalLink,
  ],
  company: [
    // About is NEW to the DE footer — needs footer.about_us.href.
    aboutLink("/ueber-uns", "footer.about_us.href"),
    {
      labelKey: "footer.affiliate.text",
      defaultLabel: "Become an Affiliate",
      hrefKey: "footer.affiliate.href",
      href: "/affiliate-programm",
    },
    {
      labelKey: "footer.community_program.text",
      defaultLabel: "Community Program",
      hrefKey: "footer.community_program.href",
      href: "/lassie-community",
    },
    contactLink("/kontaktmoeglichkeiten"),
    {
      labelKey: "footer.referral_program.text",
      defaultLabel: "Refer a friend",
      hrefKey: "footer.referral_program.href",
      href: "/freunde-werben-freunde",
    },
  ],
  legal: [
    {
      labelKey: "footer.accessibility.text",
      defaultLabel: "Accessibility in Lassie",
      hrefKey: "footer.accessibility.href",
      href: "https://de.lassie.co/barrierefreiheit",
    },
    {
      labelKey: "footer.cookie_policy.text",
      defaultLabel: "Cookie Policy",
      hrefKey: "footer.cookie_policy.href",
      href: "https://de.lassie.co/cookie-richtlinie",
    },
    {
      labelKey: "footer.imprint.text",
      defaultLabel: "Imprint",
      hrefKey: "footer.imprint.href",
      href: "https://de.lassie.co/impressum",
    },
    {
      labelKey: "footer.privacy_policy.text",
      defaultLabel: "Privacy Policy",
      hrefKey: "footer.privacy_policy.href",
      href: "https://de.lassie.co/datenschutzbestimmungen",
    },
    PRIVACY_SETTINGS,
    {
      labelKey: "footer.tac.text",
      defaultLabel: "Terms and Conditions",
      hrefKey: "footer.tac.href",
      href: "https://de.lassie.co/versicherungsbedingungen",
    },
  ],
  app: {
    appStore: "https://apps.apple.com/de/app/lassie/id1533808157",
    googlePlay:
      "https://play.google.com/store/apps/details?id=com.teamlassie.lassieapp",
  },
  social: {
    facebook: "https://www.facebook.com/lassie.germany",
    instagram: "https://www.instagram.com/lassie_de/",
    linkedin: "https://www.linkedin.com/company/lassieco/",
    youtube: "https://www.youtube.com/channel/UCBS4ArF2BlR2TixpopcOrnQ",
  },
};

const fr: Market = {
  // NEW to the footer — label reuses navigation key; href needs footer.offer_*.href.
  offer: [
    {
      labelKey: "navigation.cat_insurance",
      defaultLabel: "Cat Insurance",
      hrefKey: "footer.offer_cat_insurance.href",
      href: "/assurance-pour-chats",
    },
    {
      labelKey: "navigation.dog_insurance",
      defaultLabel: "Dog Insurance",
      hrefKey: "footer.offer_dog_insurance.href",
      href: "/assurance-pour-chiens",
    },
  ],
  support: [
    // REUSE: old FR footer used these literal docs URLs (insurance_documents.*
    // are label-only in Lokalise — no .href).
    {
      labelKey: "insurance_documents.claims",
      defaultLabel: "Care Sheet",
      href: "https://docs.lassie.co/fra/feuille-de-soins.pdf",
    },
    // NEW to the footer — needs footer.how_to_claim (text + href).
    howToClaimLink("/se-faire-rembourser"),
    {
      labelKey: "insurance_documents.ipid",
      defaultLabel: "Insurance Product Information Document",
      href: "https://docs.lassie.co/fra/latest/ipid.pdf",
    },
    withdrawalLink,
  ],
  company: [
    // About is NEW to the FR footer — needs footer.about_us.href.
    aboutLink("/a-propos-de-lassie", "footer.about_us.href"),
    {
      labelKey: "footer.affiliate.text",
      defaultLabel: "Become an Affiliate",
      hrefKey: "footer.affiliate.href",
      href: "/programme-daffiliation",
    },
    contactLink("/contactez-nous"),
    {
      labelKey: "footer.collab.text",
      defaultLabel: "Press and Partnerships",
      hrefKey: "footer.collab.href",
      href: "https://5gm6mu38ztx.typeform.com/to/XjzudP1W",
    },
    {
      labelKey: "footer.referral_program.text",
      defaultLabel: "Sponsorship",
      hrefKey: "footer.referral_program.href",
      href: "/parrainage",
    },
  ],
  legal: [
    {
      labelKey: "footer.accessibility.text",
      defaultLabel: "Accessibility at Lassie",
      hrefKey: "footer.accessibility.href",
      href: "https://fr.lassie.co/accessibilite",
    },
    {
      labelKey: "footer.cookie_policy.text",
      defaultLabel: "Cookie Management",
      hrefKey: "footer.cookie_policy.href",
      href: "https://fr.lassie.co/gestion-des-cookies",
    },
    {
      labelKey: "footer.privacy_policy.text",
      defaultLabel: "Privacy Policy",
      hrefKey: "footer.privacy_policy.href",
      href: "https://fr.lassie.co/politique-de-confidentialite",
    },
    // REUSE: the old FR footer used insurance_documents.legal for this PDF
    // ("Mentions Légales" / "Legal Notice") — label-only key, no .href.
    {
      labelKey: "insurance_documents.legal",
      defaultLabel: "Legal Notice",
      href: "https://docs.lassie.co/fra/latest/mentions-legales.pdf",
    },
    PRIVACY_SETTINGS,
    {
      // Points to the Contentful terms page (lists current + previous CGs), the
      // FR equivalent of SE's /villkor — not the CG PDF directly.
      labelKey: "insurance_documents.terms_and_conditions",
      defaultLabel: "Terms and Conditions",
      href: "https://fr.lassie.co/conditions-generales",
    },
    {
      labelKey: "insurance_documents.cgu",
      defaultLabel: "Terms of Use",
      href: "https://docs.lassie.co/fra/latest/cgu.pdf",
    },
  ],
  app: {
    appStore: "https://apps.apple.com/fr/app/lassie/id1533808157",
    googlePlay:
      "https://play.google.com/store/apps/details?id=com.teamlassie.lassieapp",
  },
  social: {
    facebook: "https://www.facebook.com/people/Lassie-France/61558443117226",
    instagram: "https://www.instagram.com/lassie_fr/",
    linkedin: "https://www.linkedin.com/company/lassieco/",
    // FR has no YouTube account — slot intentionally omitted.
  },
};

// UK (GBR) footer. Mirrors the shared cross-market footer links (the ones
// present in SE + DE + FR), using the UK targets. Only links whose UK page
// actually exists are wired — no dead links. All labels reuse existing shared
// Lokalise keys; hrefs use the shared org-context keys (which resolve their
// `_gbr` value for UK) where that value matches the UK page, else a literal.
// App badges + socials still reuse SE (no UK-specific accounts yet).
const gb: Market = {
  // hrefKeys resolve to /cat-insurance and /dog-insurance for UK (base value,
  // no _gbr override needed) — matches the UK Contentful page slugs.
  offer: [
    {
      labelKey: "navigation.cat_insurance",
      defaultLabel: "Cat Insurance",
      hrefKey: "footer.offer_cat_insurance.href",
      href: "/cat-insurance",
    },
    {
      labelKey: "navigation.dog_insurance",
      defaultLabel: "Dog Insurance",
      hrefKey: "footer.offer_dog_insurance.href",
      href: "/dog-insurance",
    },
  ],
  support: [
    // The UK claim page moved to /how-to-submit-a-claim in the slug
    // unification, matching the other three markets, so the shared key now
    // resolves to the right page for every market.
    {
      labelKey: "footer.how_to_claim.text",
      defaultLabel: "How to file a claim",
      hrefKey: "footer.how_to_claim.href",
      href: "/how-to-submit-a-claim",
    },
    // NEW to the footer for UK — label reuses navigation.faq; footer.faq.href
    // isn't in Lokalise, so the fallback (UK FAQ page slug) resolves.
    {
      labelKey: "navigation.faq",
      defaultLabel: "FAQ",
      hrefKey: "footer.faq.href",
      href: "/frequently-asked-questions",
    },
    // The three UK IPIDs (Bronze/Silver/Gold), tiered by plan. Literal doc URLs
    // from gbrIpidByPlan — the SE dogIpidIndex source resolves to the wrong
    // (Swedish) PDFs for GBR. Labels share a first word, so order pins the tier
    // sequence (Bronze→Silver→Gold) past the alphabetical sort.
    {
      labelKey: "insurance_documents.ipid_bronze",
      defaultLabel: "Insurance Product Information Document – Bronze",
      href: gbrIpidByPlan.small,
      order: 1,
    },
    {
      labelKey: "insurance_documents.ipid_silver",
      defaultLabel: "Insurance Product Information Document – Silver",
      href: gbrIpidByPlan.medium,
      order: 2,
    },
    {
      labelKey: "insurance_documents.ipid_gold",
      defaultLabel: "Insurance Product Information Document – Gold",
      href: gbrIpidByPlan.large,
      order: 3,
    },
  ],
  company: [
    // UK moved from /about-lassie to /about-us in the slug unification, so the
    // shared key is now correct here and the override is gone.
    aboutLink("/about-us", "footer.about_us.href"),
    contactLink("/contact-us", "footer.contact_us.href"),
    // UK moved from /referral-discount to /referral-program in the slug
    // unification, joining DE/FR/SE on the base value.
    {
      labelKey: "footer.referral_program.text",
      defaultLabel: "Refer a friend",
      hrefKey: "footer.referral_program.href",
      href: "/referral-program",
    },
    {
      labelKey: "footer.tractive_program.text",
      defaultLabel: "Tractive Programme",
      href: "/tractive-lassie",
    },
  ],
  // Legal links reuse the shared keys; each footer.*.href resolves its _gbr
  // value for UK (/accessibility, /cookie-policy, /privacy-policy,
  // /terms-and-conditions on uk.lassie.co).
  legal: [
    {
      labelKey: "footer.accessibility.text",
      defaultLabel: "Accessibility at Lassie",
      hrefKey: "footer.accessibility.href",
      href: "/accessibility",
    },
    {
      labelKey: "footer.cookie_policy.text",
      defaultLabel: "Cookie Policy",
      hrefKey: "footer.cookie_policy.href",
      href: "/cookie-policy",
    },
    // UK policy wording (the full policy document PDF), distinct from the
    // footer.terms_of_use link below which is the Terms of use webpage. NEW label key —
    // fallback renders "Policy wording" until it's added in Lokalise. Sorts
    // alphabetically right after Cookie Policy.
    {
      labelKey: "footer.policy_wording.text",
      defaultLabel: "Policy wording",
      href: gbrPolicyWording,
    },
    {
      labelKey: "footer.privacy_policy.text",
      defaultLabel: "Privacy Policy",
      hrefKey: "footer.privacy_policy.href",
      href: "/privacy-policy",
    },
    PRIVACY_SETTINGS,
    {
      // Terms of use webpage. The href is en_GB href_gbr in Lokalise: en_GB is
      // shared with the other markets' English trees.
      labelKey: "footer.terms_of_use.text",
      defaultLabel: "Terms of use",
      hrefKey: "footer.terms_of_use.href",
      href: "/terms-and-conditions",
      pinLast: true,
    },
    {
      labelKey: "footer.referral_program_terms.text",
      defaultLabel: "Referral Programme Terms",
      hrefKey: "footer.referral_program_terms.href",
      href: "/refer-a-friend-terms",
    },
    {
      labelKey: "footer.tractive_program_terms.text",
      defaultLabel: "Tractive Programme Terms",
      hrefKey: "footer.tractive_program_terms.href",
      href: "/tractive-terms",
    },
  ],
  legalParagraph: {
    key: "footer.info",
    defaultValue:
      "Lassie Pet Health Ltd is an appointed representative of Advent Solutions Management Ltd, which is authorised and regulated by the Financial Conduct Authority (FRN 308751). Trustpilot rating based on Lassie Group's global Trustpilot rating as at July 2026.",
  },
  app: {
    appStore: "https://apps.apple.com/gb/app/lassie/id1533808157",
    googlePlay:
      "https://play.google.com/store/apps/details?id=com.teamlassie.lassieapp",
  },
  social: {
    facebook: "https://www.facebook.com/profile.php?id=61586107754432",
    instagram: "https://www.instagram.com/lassie_uk",
    linkedin: "https://www.linkedin.com/company/lassieco/",
  },
};

const MARKETS: Record<Org, Market> = {
  swe: se,
  ger: de,
  fra: fr,
  gbr: gb,
};

const SOCIAL_ORDER: Social[] = ["facebook", "instagram", "linkedin", "youtube"];

const SOCIAL_ICONS: Record<
  Social,
  (props: { className?: string }) => ReactElement
> = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  linkedin: LinkedinIcon,
  youtube: YoutubeIcon,
};

const SOCIAL_LABELS: Record<Social, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  youtube: "YouTube",
};

/** Uppercase the first character so every label/heading starts capitalised
 *  regardless of how it's cased in Lokalise. */
const capitalize = (s: string): string =>
  s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;

const isExternal = (href: string): boolean =>
  /docs\.lassie\.co|typeform\.com|apps\.apple\.com|play\.google\.com|fi\.se/.test(
    href,
  );

const openConsentManager = (): void => {
  // Usercentrics CMP — opens the cookie-consent second layer.
  // @ts-ignore — injected by the Usercentrics script at runtime.
  window.UC_UI?.showSecondLayer();
};

export default function FooterRedesign(): ReactElement {
  const { t } = useWebTranslation();
  const { locale, defaultLocale } = useRouter();
  const market = MARKETS[getOrg()];

  const label = (link: FooterLink): string =>
    capitalize(t(link.labelKey, { defaultValue: link.defaultLabel }));

  // Factsheet hrefs come from packages().dog[i].ipid — the same Lokalise-backed
  // source the old footer used.
  const dogPackages = packages().dog;

  // href resolution mirrors the pre-redesign footer: dynamic ipid for
  // factsheets, Lokalise .href (org-context resolved) where the key has one,
  // else the fallback literal. The resolved value is a market-relative path, so
  // localeHref adds the locale prefix for a market's secondary tree — these
  // render as plain <a>, which (unlike next/link) does not do it for us.
  const hrefOf = (link: FooterLink): string => {
    if (link.dogIpidIndex != null) {
      return dogPackages[link.dogIpidIndex]?.ipid ?? link.href ?? "";
    }
    const href = link.hrefKey
      ? t(link.hrefKey, { defaultValue: link.href ?? "" })
      : (link.href ?? "");

    return localeHref(href, { locale, defaultLocale });
  };

  const sortLinks = (links: FooterLink[]): FooterLink[] =>
    [...links].sort((a, b) => {
      if (!!a.pinLast !== !!b.pinLast) return a.pinLast ? 1 : -1;
      const keyOf = (link: FooterLink): string =>
        link.order != null
          ? `${label(link).toLowerCase().split(/\s+/)[0]}${link.order}`
          : label(link).toLowerCase();
      return keyOf(a).localeCompare(keyOf(b));
    });

  const renderLink = (link: FooterLink, className: string): ReactElement => {
    const linkClass = classNames(className, { [styles.linkBold]: link.bold });
    if (link.consent) {
      return (
        <button
          type="button"
          className={linkClass}
          onClick={openConsentManager}
        >
          {label(link)}
        </button>
      );
    }
    const href = hrefOf(link);
    const external = isExternal(href);
    return (
      <a
        href={href}
        className={linkClass}
        {...(external ? { target: "_blank", rel: "noopener" } : {})}
      >
        {label(link)}
      </a>
    );
  };

  const renderColumn = (
    headingKey: string,
    defaultHeading: string,
    links: FooterLink[],
    columnClass: string,
  ): ReactElement | null => {
    // Hide the whole column when it has no links (e.g. the UK footer), so we
    // never render a dangling heading over an empty list.
    if (links.length === 0) return null;
    const heading = capitalize(t(headingKey, { defaultValue: defaultHeading }));
    const headingId = `footer-${columnClass}`;
    return (
      <div className={classNames(styles.column, styles[columnClass])}>
        <h2 id={headingId} className={styles.columnHeading}>
          {heading}
        </h2>
        <nav aria-labelledby={headingId}>
          <ul>
            {sortLinks(links).map((link) => (
              <li key={link.labelKey}>{renderLink(link, styles.link)}</li>
            ))}
          </ul>
        </nav>
      </div>
    );
  };

  const socials = SOCIAL_ORDER.filter((name) => market.social[name]);

  // The first word gets the brand gradient; the rest is solid. Splitting the
  // single translated string keeps the gradient on the lead word in every
  // language without needing separate lead/rest keys.
  const tagline = t("footer.tagline", {
    defaultValue: "More than just insurance.",
  });
  const [taglineLead, ...taglineRest] = tagline.split(" ");

  const downloadCaption = t("navigation.download_app_cta.label", {
    defaultValue: "Download Lassie App",
  });

  return (
    <footer role="contentinfo" className={styles.footer}>
      <div className={styles.inner}>
        {/* Brand row */}
        <div className={styles.brandRow}>
          <div className={styles.brandLeft}>
            <a
              href="/"
              className={styles.logo}
              aria-label={t("global.lassie", { defaultValue: "Lassie" })}
            >
              <Image
                width={158}
                height={38}
                src="/images/logo-with-name-darkblue.svg"
                alt="Lassie"
                loading="lazy"
              />
            </a>
            <p className={styles.tagline}>
              <span className={styles.taglineLead}>{taglineLead}</span>
              {taglineRest.length > 0 ? ` ${taglineRest.join(" ")}` : ""}
            </p>
          </div>
          <div className={styles.languageSelector}>
            <LanguageSelector />
          </div>
        </div>

        {/* Link columns */}
        <div className={styles.columns}>
          {renderColumn(
            "footer.our_offer",
            "Our Offer",
            market.offer,
            "colOffer",
          )}
          {renderColumn(
            "footer.support",
            "Support",
            market.support,
            "colSupport",
          )}
          {renderColumn(
            "footer.lassie",
            "Lassie",
            market.company,
            "colCompany",
          )}
        </div>

        {/* App row */}
        <div className={styles.appRow}>
          <div className={styles.qrWrap}>
            <Image
              className={styles.qr}
              width={80}
              height={80}
              src="/images/qr-code.svg"
              alt={downloadCaption}
              loading="lazy"
            />
            <span className={styles.qrCaption}>{downloadCaption}</span>
          </div>
          <div className={styles.storeBadges}>
            <a
              className={styles.storeButton}
              href={market.app.appStore}
              target="_blank"
              rel="noopener"
              aria-label="Download on the App Store"
            >
              <AppStoreIcon className={styles.storeIcon} />
              <span>App Store</span>
            </a>
            <a
              className={styles.storeButton}
              href={market.app.googlePlay}
              target="_blank"
              rel="noopener"
              aria-label="Get it on Google Play"
            >
              <PlayStoreIcon className={styles.storeIcon} />
              <span>Google Play</span>
            </a>
          </div>
          <div className={styles.socials}>
            {socials.map((name) => {
              const Glyph = SOCIAL_ICONS[name];
              return (
                <a
                  key={name}
                  className={styles.social}
                  href={market.social[name]}
                  target="_blank"
                  rel="noopener"
                  aria-label={`Lassie on ${SOCIAL_LABELS[name]}`}
                >
                  <Glyph className={styles.socialIcon} />
                </a>
              );
            })}
          </div>
        </div>

        {/* Legal block */}
        <div className={styles.legal}>
          <nav className={styles.legalLinks} aria-label="Legal">
            {sortLinks(market.legal).map((link) => (
              <React.Fragment key={link.labelKey}>
                {renderLink(link, styles.legalLink)}
              </React.Fragment>
            ))}
          </nav>
          {market.legalParagraph && (
            <p className={styles.legalParagraph}>
              {t(market.legalParagraph.key, {
                defaultValue: market.legalParagraph.defaultValue,
              })}
            </p>
          )}
          <p className={styles.copyright}>
            {t("footer.rights", { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>
    </footer>
  );
}
